import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ResultPosterTile, { hasConsent } from './ResultPosterTile';
import { resultCards } from '../../data/results';

/*
 * Homepage "featured results" rail. CSS scroll-snap does the core work —
 * overflow-x-auto + snap-x + snap-start on each card, native momentum
 * scrolling, zero JS required to swipe on touch. Desktop additionally gets
 * prev/next buttons that scrollBy one card width; each button is hidden
 * outright (not just disabled) once there is nothing left to scroll to in
 * that direction, per spec.
 *
 * Renders ResultPosterTile — the same component, schema and consent gate that
 * /results uses. This is not a second design.
 *
 * The tiles were ResultCard until the owner chose the poster treatment;
 * ResultCard.js stays in the tree, unused, for a future restyle.
 *
 * FEATURED_ORDER is a deliberate, curated sequence — NOT prestige-ordered.
 * It deliberately spreads across achievement types (overseas admission, IIT,
 * a JEE percentile, a CBSE Class 10 board result, a multi-offer story) so a
 * CBSE-board parent sees a result that looks like their own child's, not
 * only overseas admissions. Do not resort this by score, batch, or
 * alphabetically — ask before changing the order.
 */
const FEATURED_ORDER = [
  'vihaan-chopra', // CBSE Class 10 2025-26, a perfect 100 in Maths — newest and strongest
  'arnav-gulati', // NUS + Imperial
  'arnav-vivek', // IIT Jodhpur
  'abhinav-ray', // JEE Main 99.14 percentile
  'aadya-pathak', // CBSE Class 10 2025-26, 98 Science / 96 Maths
  'ritisha-kapoor', // four selections
];

const cardsBySlug = new Map(resultCards.map((entry) => [entry.slug, entry]));

const featuredCards = FEATURED_ORDER.map((slug) => cardsBySlug.get(slug)).filter(
  (entry) => entry?.featured === true && hasConsent(entry) && Boolean(entry.posterSrc)
);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const ResultsRail = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByCard = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector('[data-rail-card]');
    const step = (firstCard?.offsetWidth || el.clientWidth * 0.8) + 20; // + gap
    el.scrollBy({ left: direction * step, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  // Nothing consented yet: no rail, no empty-state chrome on the homepage —
  // the empty state belongs to /results, which is the page about results.
  if (featuredCards.length === 0) return null;

  return (
    <section className="scroll-mt-16 lg:scroll-mt-20 bg-brand-mist">
      <div className="mx-auto max-w-[1180px] py-9 pl-5 lg:px-11 lg:py-[76px]">
        <div className="lg:flex lg:items-end lg:justify-between lg:mb-[34px] pr-5 lg:pr-0">
          <div>
            <h2 className="text-[25px] lg:text-[38px] font-extrabold leading-tight text-brand-deep tracking-[-0.02em] lg:tracking-[-0.025em] mb-1.5 lg:mb-2">
              Recent results
            </h2>
            <p className="text-sm lg:text-[15.5px] text-brand-slate mb-5 lg:mb-0">
              Board marks, entrance scores and university admissions — 2020-21 to 2024-25.
            </p>
          </div>

          {/* Desktop-only prev/next; mobile relies on touch/swipe alone. */}
          <div className="hidden lg:flex items-center gap-2">
            {canScrollLeft && (
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                aria-label="Scroll featured results left"
                className="w-10 h-10 rounded-full border border-brand-line flex items-center justify-center text-brand-deep hover:border-brand-deep transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {canScrollRight && (
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                aria-label="Scroll featured results right"
                className="w-10 h-10 rounded-full border border-brand-line flex items-center justify-center text-brand-deep hover:border-brand-deep transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div
          ref={scrollRef}
          tabIndex={0}
          role="region"
          aria-label="Featured student results, scroll for more"
          className="flex gap-3 lg:gap-5 overflow-x-auto snap-x snap-mandatory pr-5 pb-1.5 lg:pr-0 lg:pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {featuredCards.map((entry) => (
            <div
              key={entry.slug}
              data-rail-card
              className="flex-none w-[268px] sm:w-[300px] lg:w-[330px] snap-start"
            >
              <ResultPosterTile entry={entry} eager />
            </div>
          ))}

          <Link
            to="/results"
            className="flex-none w-[268px] sm:w-[300px] lg:w-[330px] snap-start border border-brand-line rounded-[14px] lg:rounded-2xl bg-white flex flex-col items-center justify-center gap-3 text-center p-6 hover:border-brand-deep hover:shadow-[0_6px_20px_rgba(11,58,69,.1)] transition-all"
          >
            <span className="w-11 h-11 rounded-full bg-brand-tint text-brand-deep flex items-center justify-center">
              <ArrowRight className="w-5 h-5" />
            </span>
            <span className="text-[15px] font-bold text-brand-deep">See all results</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ResultsRail;
